import {
  datadogRum,
  type DatadogRum,
  DefaultPrivacyLevel,
  RumInitConfiguration,
  type AddDurationVitalOptions,
  type DurationVitalOptions,
  type ViewOptions,
} from '@datadog/browser-rum';
import { TrackingConsent, type User } from '@datadog/browser-core';
import { addConsentListener, getConsentState } from '../oneTrust/oneTrust';

let consentListenerId: symbol | null = null;

export interface RumClient {
  addAction?: (name: string, context?: Record<string, unknown>) => void;
  addError?: (error: Error, context?: Record<string, unknown>) => void;
  addTiming?: (name: string, time?: number) => void;
  startView?: (options: { name: string }) => void;
}

declare global {
  interface Window {
    DD_RUM?: RumClient;
  }
}

/**
 * Init Real User Monitoring from DataDog. Currently supporting sdk version ^7.5.0
 * for a full list of configuration refer to the DataDog documentation
 * https://docs.datadoghq.com/real_user_monitoring/browser/
 *
 * This method automatically enforces user tracking and cookie consent meaning that
 * reporting might turn off/on itself depending on users preferences.
 *
 * When consent is unknown (no OneTrust cookie), the SDK initializes in deferred
 * mode — buffering events until consent is granted via OneTrustGroupsUpdated.
 *
 * @param initConfiguration initial configuration
 */
export function initializeRum(initConfiguration: RumInitConfiguration) {
  const { hasAnalyticsConsent } = getConsentState();

  const fixedConfigurations = {
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: DefaultPrivacyLevel.MASK,
  };

  // Initialize RUM immediately, but prevent collection until OneTrust grants
  // analytics consent. The Datadog SDK treats an omitted trackingConsent as granted.
  datadogRum.init({
    ...initConfiguration,
    ...fixedConfigurations,
    trackingConsent: hasAnalyticsConsent ? TrackingConsent.GRANTED : TrackingConsent.NOT_GRANTED,
  });

  consentListenerId ??= addConsentListener(
    ({ hasAnalyticsConsent: consent }) => {
      if (consent) datadogRum.setTrackingConsent(TrackingConsent.GRANTED);
      else datadogRum.setTrackingConsent(TrackingConsent.NOT_GRANTED);
    }
  );
}

/**
 * Returns the instance of the datadogRum object.
 *
 * @returns datadogRum instance
 */
export function getDatadogRumInstance(): DatadogRum {
  return datadogRum;
}

// ---------------------------------------------------------------------------
// Convenience wrappers — use these instead of declaring window.DD_RUM in every
// micro-frontend. They delegate to the client initialized by the shell when it
// is available, allowing separately bundled plugins to share one RUM session.
// ---------------------------------------------------------------------------

function getRumClient(): DatadogRum {
  if (typeof window !== 'undefined' && window.DD_RUM) {
    return window.DD_RUM as DatadogRum;
  }

  return datadogRum;
}

export function startView(nameOrOptions?: string | ViewOptions): void {
  getRumClient().startView(nameOrOptions);
}

export function addAction(name: string, context?: object): void {
  getRumClient().addAction(name, context);
}

export function addError(error: unknown, context?: object): void {
  getRumClient().addError(error, context);
}

export function addTiming(name: string, time?: number): void {
  getRumClient().addTiming(name, time);
}

export function startDurationVital(name: string, options?: DurationVitalOptions): void {
  getRumClient().startDurationVital(name, options);
}

export function stopDurationVital(name: string, options?: DurationVitalOptions): void {
  getRumClient().stopDurationVital(name, options);
}

export function setViewContextProperty(key: string, value: unknown): void {
  getRumClient().setViewContextProperty(key, value);
}

export function setUser(user: User & { id: string }): void {
  getRumClient().setUser(user);
}

export function setUserProperty(key: string, value: unknown): void {
  getRumClient().setUserProperty(key, value);
}

export function setGlobalContextProperty(key: string, value: unknown): void {
  getRumClient().setGlobalContextProperty(key, value);
}

export function addDurationVital(name: string, options: AddDurationVitalOptions): void {
  getRumClient().addDurationVital(name, options);
}
